import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Users,
  AlertCircle,
  BarChart3,
  PieChart,
  Calendar
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const FinancialReportsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  // Mock data for KPIs
  const [kpis, setKpis] = useState({});
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      setKpis({
        totalRevenue: 150000,
        totalExpenses: 95000,
        netProfit: 55000,
        totalSalaries: 77300,
        invoiceCollectionRate: 85,
        overdueInvoices: 59500
      });

      // Mock monthly data for chart
      setMonthlyData([
        { month: 'يناير', revenue: 120000, expenses: 80000 },
        { month: 'فبراير', revenue: 135000, expenses: 85000 },
        { month: 'مارس', revenue: 145000, expenses: 90000 },
        { month: 'أبريل', revenue: 150000, expenses: 95000 }
      ]);

      setLoading(false);
    }, 500);
  }, [period]);

  const reportCards = [
    {
      title: 'تقارير الإيرادات',
      description: 'تفاصيل الإيرادات حسب الفترة والخدمة والعميل',
      icon: TrendingUp,
      color: 'green',
      path: '/admin/finance/reports/revenues'
    },
    {
      title: 'تقارير المصروفات',
      description: 'تحليل المصروفات حسب التصنيف والجهة',
      icon: TrendingDown,
      color: 'red',
      path: '/admin/finance/reports/expenses'
    },
    {
      title: 'تقارير الربحية',
      description: 'ربحية حسب العميل والقضية ونوع الخدمة',
      icon: DollarSign,
      color: 'blue',
      path: '/admin/finance/reports/profitability'
    },
    {
      title: 'تقارير الرواتب',
      description: 'التكاليف البشرية والرواتب حسب القسم',
      icon: Users,
      color: 'purple',
      path: '/admin/finance/reports/payroll'
    },
    {
      title: 'تقارير التحصيل',
      description: 'أعمار الديون ونسبة التحصيل',
      icon: AlertCircle,
      color: 'orange',
      path: '/admin/finance/reports/collection'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل التقارير...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('finance.reports.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول للتقارير المالية</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <BarChart3 className="w-8 h-8" />
                <span>التقارير المالية</span>
              </h1>
              <p className="text-gray-600">نظرة شاملة على الأداء المالي للمنصة</p>
            </div>

            {/* Period Filter */}
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="week">هذا الأسبوع</option>
                <option value="month">هذا الشهر</option>
                <option value="quarter">هذا الربع</option>
                <option value="year">هذا العام</option>
              </select>
            </div>
          </div>
        </div>

        {/* KPIs - المؤشرات الرئيسية */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Revenue */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الإيرادات</p>
                <p className="text-3xl font-black text-green-600">{kpis.totalRevenue.toLocaleString()} ر.س</p>
              </div>
              <TrendingUp className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          {/* Total Expenses */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي المصروفات</p>
                <p className="text-3xl font-black text-red-600">{kpis.totalExpenses.toLocaleString()} ر.س</p>
              </div>
              <TrendingDown className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          {/* Net Profit/Loss */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#1E3A8A] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">صافي الربح/الخسارة</p>
                <p className={`text-3xl font-black ${kpis.netProfit >= 0 ? 'text-[#1E3A8A]' : 'text-red-600'}`}>
                  {kpis.netProfit.toLocaleString()} ر.س
                </p>
              </div>
              <DollarSign className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Total Salaries */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-purple-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الرواتب</p>
                <p className="text-2xl font-black text-purple-600">{kpis.totalSalaries.toLocaleString()} ر.س</p>
              </div>
              <Users className="w-10 h-10 text-purple-600 opacity-20" />
            </div>
          </div>

          {/* Invoice Collection Rate */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">نسبة تحصيل الفواتير</p>
                <p className="text-3xl font-black text-[#C8A870]">{kpis.invoiceCollectionRate}%</p>
              </div>
              <FileText className="w-12 h-12 text-[#C8A870] opacity-20" />
            </div>
          </div>

          {/* Overdue Invoices */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الفواتير المتأخرة</p>
                <p className="text-2xl font-black text-orange-600">{kpis.overdueInvoices.toLocaleString()} ر.س</p>
              </div>
              <AlertCircle className="w-10 h-10 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Monthly Chart - مخطط شهري */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#1E3A8A]" />
            الإيرادات والمصروفات الشهرية
          </h3>
          
          <div className="space-y-4">
            {monthlyData.map((item, index) => {
              const maxValue = Math.max(...monthlyData.map(d => Math.max(d.revenue, d.expenses)));
              const revenueWidth = (item.revenue / maxValue) * 100;
              const expenseWidth = (item.expenses / maxValue) * 100;

              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-gray-700 w-20">{item.month}</span>
                    <div className="flex-1 mx-4">
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-green-600">إيرادات</span>
                          <span className="text-xs font-bold text-green-600">{item.revenue.toLocaleString()} ر.س</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-green-500 h-4 rounded-full transition-all duration-500"
                            style={{ width: `${revenueWidth}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-red-600">مصروفات</span>
                          <span className="text-xs font-bold text-red-600">{item.expenses.toLocaleString()} ر.س</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-red-500 h-4 rounded-full transition-all duration-500"
                            style={{ width: `${expenseWidth}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-[#1E3A8A] w-32 text-left">
                      صافي: {(item.revenue - item.expenses).toLocaleString()} ر.س
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Report Cards */}
        <div>
          <h3 className="text-xl font-black text-gray-800 mb-4">التقارير التفصيلية</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportCards.map((card, index) => {
              const Icon = card.icon;
              const colorClasses = {
                green: 'border-green-200 hover:border-green-400 hover:bg-green-50',
                red: 'border-red-200 hover:border-red-400 hover:bg-red-50',
                blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
                purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
                orange: 'border-orange-200 hover:border-orange-400 hover:bg-orange-50'
              };

              const iconColorClasses = {
                green: 'text-green-600',
                red: 'text-red-600',
                blue: 'text-blue-600',
                purple: 'text-purple-600',
                orange: 'text-orange-600'
              };

              return (
                <button
                  key={index}
                  onClick={() => navigate(card.path)}
                  className={`bg-white rounded-lg shadow-sm border-2 p-6 text-right transition-all ${colorClasses[card.color]}`}
                >
                  <Icon className={`w-10 h-10 mb-3 ${iconColorClasses[card.color]}`} />
                  <h4 className="text-lg font-black text-gray-800 mb-2">{card.title}</h4>
                  <p className="text-sm text-gray-600">{card.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default FinancialReportsPage;
